import React from 'react';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { useRateio } from '../hooks/useRateio';
import { fmtCNPJ } from '../utils/formatters';
import { fmtPercentual } from '../utils/rateioDoGrupo';
import { PainelDoGrupo, montarPainelDoGrupo } from '../utils/quemUsaQuemPaga';

const brl = (v: number) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Um tom por empresa, do mais forte (quem mais usa) ao mais claro.
const TONS = ['#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4', '#0f766e', '#115e59'];
// Tons claros (índices 2 a 4) pedem texto escuro.
const textoSobre = (i: number) => ([2, 3, 4].includes(i % TONS.length) ? 'text-slate-900' : 'text-white');
const tom = (i: number) => TONS[i % TONS.length];

/**
 * A barra de proporção e a lista por empresa. Desenho único para o painel de
 * Grupos Econômicos e para a aba "O grupo" da ficha da assinatura: mesmo
 * cálculo (`montarPainelDoGrupo`), mesma barra.
 *
 * `detalhado` acrescenta CNPJ, papel (matriz/filial/coligada) e o MRR de cada
 * empresa, que a ficha mostra e o painel de grupos já tem nas linhas de baixo.
 */
export function BarraDoGrupo({ p, nome, detalhado, mostrarCalculado }: { p: PainelDoGrupo; nome: (clientId: number) => string; detalhado?: boolean; mostrarCalculado?: boolean }) {
  // Com a base mudada, ao lado do vigente vai o que a base de hoje daria e a diferença.
  const comCalculado = !!mostrarCalculado && p.divergente;
  // Soma das cotas EXIBIDAS, não o pool repetido: se a divisão não fechar, a
  // diferença aparece aqui em vez de ficar escondida.
  const somaDasCotas = Math.round(p.empresas.reduce((s, e) => s + e.valor, 0) * 100) / 100;
  const fecha = Math.abs(somaDasCotas - p.pool) < 0.005;
  return (
    <div className='space-y-3'>
      {/* Largura = ramais. Empresa sem ramal não ocupa barra, mas fica na lista. */}
      {p.ramais > 0 && (
        <div className='flex h-8 rounded-md overflow-hidden border border-slate-600' role='img' aria-label={`Ramais por empresa: ${p.empresas.map((e) => `${nome(e.clientId)} ${e.ramais}`).join(', ')}`}>
          {p.empresas.map((e, i) => e.ramais > 0 && (
            <div
              key={e.clientId}
              style={{ flex: e.ramais, background: tom(i) }}
              className={`flex items-center justify-center min-w-0 overflow-hidden whitespace-nowrap px-1 text-xs font-medium ${textoSobre(i)}`}
              title={`${nome(e.clientId)}: ${e.ramais} ${e.ramais === 1 ? 'ramal' : 'ramais'} · ${fmtPercentual(e.percentual)} · R$ ${brl(e.valor)}`}
            >
              <span className='truncate'>{e.ramais / p.ramais >= 0.2 ? `${nome(e.clientId)} ${fmtPercentual(e.percentual)}` : fmtPercentual(e.percentual)}</span>
            </div>
          ))}
        </div>
      )}

      <div className='divide-y divide-slate-800 text-sm'>
        {p.empresas.map((e, i) => (
          <div key={e.clientId} className='flex items-center gap-3 py-1.5'>
            <span className='w-2.5 h-2.5 rounded-sm shrink-0' style={{ background: e.ramais > 0 ? tom(i) : 'transparent', border: e.ramais > 0 ? undefined : '1px solid #475569' }} />
            <span className='flex-1 min-w-0 truncate text-slate-200'>
              {nome(e.clientId)}
              {detalhado ? (
                <span className='ml-1.5 text-[11px] text-slate-500'>
                  · {e.papel === 'empresa' ? 'do grupo' : e.papel}{e.cnpj ? ` · ${fmtCNPJ(e.cnpj)}` : ''}
                </span>
              ) : e.ehMatriz && <span className='ml-1.5 text-[11px] text-slate-500'>· matriz</span>}
            </span>
            <span className='w-20 text-right text-xs text-slate-400 tabular-nums'>{e.ramais} {e.ramais === 1 ? 'ramal' : 'ramais'}</span>
            <span className='w-14 text-right text-xs text-slate-400 tabular-nums'>{e.ddrs} DDR</span>
            <span className={`w-16 text-right tabular-nums ${TONE_TEXT.group}`}>{fmtPercentual(e.percentual)}</span>
            <span className={`w-24 text-right tabular-nums ${TONE_TEXT.group}`} title='Cota no compartilhado, como é cobrada (item a item)'>R$ {brl(e.valor)}</span>
            {comCalculado && (
              <>
                <span className='w-16 text-right text-xs text-slate-400 tabular-nums' title='Percentual que a base de hoje daria'>→ {fmtPercentual(e.percentualCalculado)}</span>
                {/* §11.2: negativo vermelho, positivo verde, sempre com sinal. */}
                <span
                  className={`w-24 text-right text-xs font-medium tabular-nums ${e.deltaReais < 0 ? TONE_TEXT.danger : e.deltaReais > 0 ? TONE_TEXT.open : 'text-slate-400'}`}
                  title={`Calculado R$ ${brl(e.valorCalculado)}`}
                  data-testid={`delta-${e.clientId}`}
                >
                  {e.deltaReais > 0 ? '+' : e.deltaReais < 0 ? '−' : ''}R$ {brl(Math.abs(e.deltaReais))}
                </span>
              </>
            )}
            {detalhado && <span className='w-24 text-right text-green-400 tabular-nums' title='MRR das assinaturas ativas desta empresa (cota + próprio)'>R$ {brl(e.mrr)}</span>}
          </div>
        ))}
        <div className='flex items-center gap-3 pt-2 font-medium'>
          <span className='w-2.5 shrink-0' />
          <span className='flex-1 text-slate-300'>Total</span>
          <span className='w-20 text-right text-xs text-slate-300 tabular-nums'>{p.ramais} ramais</span>
          <span className='w-14 text-right text-xs text-slate-300 tabular-nums'>{p.ddrs} DDR</span>
          <span className='w-16' />
          <span className={`w-24 text-right tabular-nums ${fecha ? TONE_TEXT.group : TONE_TEXT.danger}`} title={fecha ? 'Soma das cotas = compartilhado' : `As cotas somam R$ ${brl(somaDasCotas)}, e o compartilhado é R$ ${brl(p.pool)}`}>R$ {brl(somaDasCotas)}</span>
          {comCalculado && <span className='w-40' />}
          {detalhado && <span className='w-24 text-right text-green-400 tabular-nums'>R$ {brl(p.mrr)}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * QUEM USA, QUEM PAGA: o grupo de relance, no topo do grupo aberto em Grupos
 * Econômicos. Regras e fontes em utils/quemUsaQuemPaga.ts. Grupo sem rateio não
 * mostra nada.
 */
export default function PainelQuemUsaQuemPaga({
  clientId,
  contracts,
  clients,
  nome,
  onRedividir,
}: {
  clientId: number;
  contracts: any[];
  clients?: any[];
  nome: (clientId: number) => string;
  /** Abre o modal de redividir. Sem ele, o aviso aparece sem o botão. */
  onRedividir?: () => void;
}) {
  // Mesmo cache do PainelDoRateio logo abaixo (hooks/useRateio): uma leitura.
  // Painel acessório: sem rateio legível (erro), o resto do grupo segue visível.
  const { rateio: r } = useRateio(clientId);

  const p = montarPainelDoGrupo(r, contracts, clients);
  if (!p) return null;

  return (
    <div className='rounded-lg border border-slate-700 bg-slate-900/40 p-4 space-y-3'>
      <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
        <span className='text-sm font-semibold text-white'>Quem usa, quem paga</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium tabular-nums ${TONE_BADGE.neutral}`}>
          {p.ramais} {p.ramais === 1 ? 'ramal' : 'ramais'} · {p.ddrs} DDR
        </span>
        <span className='ml-auto text-xs text-slate-400 tabular-nums'>
          compartilhado <b className='text-slate-200'>R$ {brl(p.pool)}</b> · MRR do grupo <b className='text-green-400'>R$ {brl(p.mrr)}</b>
        </span>
      </div>
      {p.divergente && (
        <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning}`} role='status'>
          <span className={`flex-1 min-w-0 ${TONE_TEXT.warning}`}>A base mudou. A divisão vigente foi acordada com outra contagem de ramais.</span>
          {onRedividir && (
            <button type='button' onClick={onRedividir} className='px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 text-sm text-slate-200 hover:bg-slate-700'>
              Redividir
            </button>
          )}
        </div>
      )}
      <BarraDoGrupo p={p} nome={nome} mostrarCalculado />
    </div>
  );
}
